/**
 * Webメールの AI（`/api/v1/portal/ai/...`）。
 *
 * **この画面が無いあいだ、契約者は AI を止められなかった。** 有効化も
 * 接続先の指定も月次の消費量も、運営コンソール（`apps/admin/src/routes/ai.tsx`）
 * からしか触れず、契約者面の API 3 本は 1 か所からも呼ばれていなかった。
 * メール本文を LLM へ渡す機能を、渡される側の契約者が止められない状態は
 * 説明責任として成立しない（法定ページには「いつでも停止できます」と
 * 掲示済みなので、掲示だけが実態を先取りしていた）。
 *
 * ## API がそのために返している値を、そのまま出す
 *
 * `GET /ai/policy` は **`requested`（希望）と `effective`（実効）を必ず両方**
 * 返す。プランで削られたこと・運営に固定されたことが画面で分かるようにする
 * ためで（`routes/portal/ai.ts` の冒頭）、片方だけ描くと契約者は
 * 「設定したのに効かない」の理由を推測するしかなくなる。だからこの画面は
 *   - `plan.aiCustomEndpoint` が false → 接続先の入力を disabled にして理由を出す
 *   - `lockedByOps` → フォーム全体を読み取り専用にし `opsNote` をそのまま出す
 *   - `notEnforced` → 「保存はできるが Webメール側で強制できない」を明示
 *   - `routing.reason` → AI が止まっている理由を必ず出す
 * を全部描く。**隠すと必ず問い合わせになる。**
 *
 * ## 保存の形
 *
 * `PUT /ai/policy` は**部分更新**（`orgAiPolicyUpdateSchema` は partial）。
 * ただし指定できない項目を送ると 403 になるので、
 * `aiCustomEndpoint` が false のときは `provider` / `endpoint` / `preset` /
 * `apiKey` / `requireResidency` を**本文に入れない**（`buildPatch()`）。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  StatusBadge,
  SwitchRow,
  UsageBar,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import {
  AI_FEATURE,
  AI_FEATURE_LABELS_JA,
  AI_LIMIT_ACTION,
  AI_PREFERRED_REGION,
  AI_PRIVACY_LEVEL,
  AI_RESIDENCY_REQUIREMENT,
  ORG_PERMISSION,
  labelOf,
  type AiLimitAction,
  type AiPreferredRegion,
  type AiPrivacyLevel,
  type AiResidencyRequirement,
  type OrgAiPolicy,
} from '@mail-sv/shared';
import { Bot, Gauge, Lock, ShieldQuestion } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { Async } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { PlanLimitFromError } from '../components/plan-limit.js';
import { usePermission } from '../stores/auth.js';
import type { AiPolicyView, AiUsageView } from '../lib/api-types.js';

/** 上限の入力は「万トークン」で受ける（生の桁数だと打ち間違いに気付けない）。 */
const TOKENS_PER_UNIT = 10_000;

/**
 * 経路が決まらない理由（`AiRoutingRejectReason`）→ 契約者向けの日本語。
 *
 * **`labels.ja.ts` には無い語彙。** 運営向けの語彙表に足すと
 * 「接続先の在庫」という運営の設備情報の言い回しになり、契約者面には
 * 合わない（同じ状態でも、契約者に伝えるべきは「自分が何をすればよいか」）。
 * 未知の値は識別子をそのまま出す既存方針に合わせてフォールバックする。
 */
const ROUTING_REASON_JA: Record<string, string> = {
  provider_none: '利用する AI の提供元が決まっていません。',
  no_endpoint_for_provider:
    '選択された提供元に接続できる設備が用意されていません。サポートまでお問い合わせください。',
  residency_unsatisfied:
    'ご指定の国で処理できる接続先が現在ありません。別の国へは送信せず、AI を停止しています。',
  all_unhealthy: '接続先に一時的な障害が発生しています。復旧までお待ちください。',
};

export function AiPage() {
  const canWrite = usePermission(ORG_PERMISSION.write);
  const policy = useQuery({
    queryKey: qk.aiPolicy(),
    queryFn: () => api.get<AiPolicyView>('/ai/policy'),
  });

  return (
    <>
      <PageHeader
        title="Webメールの AI 機能"
        description="要約・返信文の提案などで、メールの内容を AI（大規模言語モデル）へ渡すかどうかを設定します。"
      />

      <div className="space-y-6">
        <HelpNote title="既定では無効です">
          この機能を有効にすると、対象の操作を行ったときにメールの内容が
          AI の提供元へ送信されます。いつでもこの画面から停止できます。
          送信する範囲（件名だけ／本文の冒頭だけ／本文全体）も選べます。
        </HelpNote>

        <Async query={policy} lines={6}>
          {(data) => (
            <>
              <AiStatusCards data={data} />
              <Section title="設定">
                <AiPolicyForm data={data} canWrite={canWrite} />
              </Section>
              <Section
                title="今月の使用量"
                description="AI へ渡した分・返ってきた分のトークン数です。メールの本文そのものは記録していません。"
              >
                <AiUsageCard cap={data.effective.monthlyTokenCap} />
              </Section>
            </>
          )}
        </Async>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ 状態 */

function AiStatusCards({ data }: { data: AiPolicyView }) {
  const usage = data.usage;
  const syncTone =
    data.sync.state === 'synced'
      ? 'succeeded'
      : data.sync.state === 'failed'
        ? 'failed'
        : 'pending';

  return (
    <div className="space-y-4">
      {/*
       * **プランに AI が無い組織は保存そのものができない。**
       * `PUT /ai/policy` は先頭で `assertFeature(plan, 'aiAssistant')` を通し、
       * 409 `PLAN_LIMIT_EXCEEDED` を返す（dev スタックで実測）。
       * 「設定は保存できます」と書くと、押した瞬間に赤いエラーが出る画面に
       * なるので、**フォームごと読み取り専用**にして理由を先に出す。
       */}
      {!data.plan.aiAssistant ? (
        <Alert variant="info">
          <AlertTitle>ご利用中のプランには AI 機能が含まれていません</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              この画面の設定は上位プランでご利用いただけます。現在は、メールの内容が AI
              へ送信されることはありません。
            </p>
            <Button asChild variant="outline" size="sm">
              <AppLink to="/billing/plan">プランを見る</AppLink>
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {data.lockedByOps ? (
        <Alert variant="warning">
          <AlertTitle className="flex items-center gap-2">
            <Lock className="size-4" aria-hidden />
            この組織の AI 設定は運営により固定されています
          </AlertTitle>
          <AlertDescription>
            {data.opsNote ?? '変更が必要な場合はサポートまでご連絡ください。'}
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
       * **止まっている理由は必ず出す。** レジデンシー要件を満たす接続先が
       * 無いときは「黙って別の法域へ流さず止める」のが設計どおりの挙動で、
       * 止まっていることを画面に出さないと契約者には故障にしか見えない。
       *
       * ただし**有効にしていない組織には出さない**。無効なら
       * `reason` は必ず `provider_none` になるので、そのまま描くと
       * 「使っていないのに『動作していません』と警告される」画面になる。
       */}
      {data.effective.enabled && data.routing.reason ? (
        <Alert variant="warning">
          <AlertTitle>いま AI は動作していません</AlertTitle>
          <AlertDescription>
            {ROUTING_REASON_JA[data.routing.reason] ?? data.routing.reason}
          </AlertDescription>
        </Alert>
      ) : null}

      {data.sync.state === 'failed' ? (
        <Alert variant="destructive">
          <AlertTitle>設定を Webメールへ反映できていません</AlertTitle>
          <AlertDescription>
            {data.sync.error ?? '反映に失敗しました。しばらく待って再度保存してください。'}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="いまの状態"
          value={data.effective.enabled ? '有効' : '無効'}
          icon={Bot}
          tone={data.effective.enabled ? 'success' : 'default'}
          hint={data.effective.enabled ? labelOf(data.effective.provider) : '本文は送信されません'}
        />
        <StatCard
          label="今月のトークン"
          value={formatCount(usage.totalTokens)}
          icon={Gauge}
          tone={usage.overCap ? 'destructive' : 'default'}
          hint={usage.cap > 0 ? `上限 ${formatCount(usage.cap)}` : '上限なし'}
        />
        <StatCard
          label="処理される場所"
          value={data.routing.region ? labelOf(data.routing.region) : '未定'}
          icon={ShieldQuestion}
          hint={
            data.routing.dataResidency
              ? `法域: ${labelOf(data.routing.dataResidency)}`
              : '接続先が決まっていません'
          }
        />
      </div>

      <Card>
        <CardContent className="p-5">
          <KeyValue
            columns={3}
            items={[
              {
                key: 'Webメールへの反映',
                value: (
                  <StatusBadge
                    status={syncTone}
                    label={labelOf(data.sync.state)}
                    size="sm"
                    dot
                  />
                ),
              },
              {
                key: '最終反映',
                value: data.sync.syncedAt ? formatDateTime(data.sync.syncedAt) : '—',
              },
              {
                key: '最終更新',
                value: data.updatedAt ? formatDateTime(data.updatedAt) : '未設定',
              },
            ]}
          />
        </CardContent>
      </Card>

      {/*
       * **効かない指定を隠さない。** 保存はできるが Webメール側で強制できない
       * 項目（`privacyLevel` など）を黙っていると、契約者は「本文は出さない
       * 設定にした」と信じたまま本文が出続ける。同じ壊れ方を一度作っている。
       */}
      {data.notEnforced.fields.length > 0 || data.notEnforced.features.length > 0 ? (
        <Alert variant="warning">
          <AlertTitle>Webメール側で強制できない設定があります</AlertTitle>
          <AlertDescription className="space-y-1">
            <p>{data.notEnforced.note}</p>
            <p className="text-xs">
              対象:{' '}
              {[
                ...data.notEnforced.fields.map((f) => labelOf(f)),
                ...data.notEnforced.features.map((f) => AI_FEATURE_LABELS_JA[f] ?? f),
              ].join('、')}
            </p>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ 設定 */

function AiPolicyForm({ data, canWrite }: { data: AiPolicyView; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState<OrgAiPolicy>(data.requested);
  const [apiKey, setApiKey] = React.useState('');
  // サーバの値が変わった（保存後の再取得など）ら下書きを合わせる。
  React.useEffect(() => {
    setDraft(data.requested);
  }, [data.requested]);

  /*
   * **保存できない条件を 1 か所に集める。**
   * プランに AI が無いと `PUT /ai/policy` は 409 を返す（`assertFeature`）ので、
   * 権限・運営ロックと同じ扱いにして入力ごと止める。
   */
  const locked = data.lockedByOps || !canWrite || !data.plan.aiAssistant;
  const canEndpoint = data.plan.aiCustomEndpoint;

  const save = useMutation({
    mutationFn: () => api.put<unknown>('/ai/policy', buildPatch(draft, apiKey, canEndpoint)),
    onSuccess: () => {
      setApiKey('');
      return queryClient.invalidateQueries({ queryKey: qk.aiPolicy() });
    },
  });

  const set = <K extends keyof OrgAiPolicy>(key: K, value: OrgAiPolicy[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">AI に何をさせるか</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {/* 409 `PLAN_LIMIT_EXCEEDED` は他画面と同じアップセルの見た目に寄せる。 */}
          <PlanLimitFromError error={save.error} />
          <FormAlert error={save.error} />
          {save.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>
                保存しました。Webメール側への反映は数分以内に完了します。
              </AlertDescription>
            </Alert>
          ) : null}

          <SwitchRow
            label="AI 機能を利用する"
            description="オフにすると、メールの内容が AI へ送信されることは一切なくなります。"
            checked={draft.enabled}
            disabled={locked}
            onCheckedChange={(enabled) => set('enabled', enabled)}
          />

          <Field
            label="AI へ渡す範囲"
            hint="狭いほど安全ですが、要約や返信文の品質は下がります。"
          >
            <Select
              value={draft.privacyLevel}
              onValueChange={(v) => set('privacyLevel', v as AiPrivacyLevel)}
            >
              <SelectTrigger disabled={locked || !draft.enabled}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_PRIVACY_LEVEL.map((lv) => (
                  <SelectItem key={lv} value={lv}>
                    {labelOf(lv)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="使う機能" hint="使わない機能を切っておくと、その分だけ本文が渡りません。">
            <div className="space-y-2">
              {AI_FEATURE.map((f) => (
                <SwitchRow
                  key={f}
                  label={AI_FEATURE_LABELS_JA[f] ?? f}
                  checked={draft.features[f]}
                  disabled={locked || !draft.enabled}
                  onCheckedChange={(on) =>
                    set('features', { ...draft.features, [f]: on })
                  }
                />
              ))}
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="今月の上限（万トークン）"
              htmlFor="ai-cap"
              hint="0 を入れるとプランの上限まで使えます。"
            >
              <Input
                id="ai-cap"
                type="number"
                min={0}
                step={1}
                value={String(Math.round(draft.monthlyTokenCap / TOKENS_PER_UNIT))}
                disabled={locked || !draft.enabled}
                onChange={(e) =>
                  set(
                    'monthlyTokenCap',
                    Math.max(0, Math.floor(Number(e.target.value) || 0)) * TOKENS_PER_UNIT,
                  )
                }
              />
            </Field>

            <Field
              label="上限に達したとき"
              hint="「従量課金で継続する」を選ぶと、超過分がご請求に加算されます。"
            >
              <Select
                value={draft.onLimit}
                onValueChange={(v) => set('onLimit', v as AiLimitAction)}
              >
                <SelectTrigger disabled={locked || !draft.enabled}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_LIMIT_ACTION.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a === 'block' ? '停止する（追加料金なし）' : labelOf(a)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/*
           * **接続先はプランで閉じている契約者にも見せる（disabled で）。**
           * ナビや項目ごと消すと「対応していない」ようにしか見えず、
           * 上位プランを検討する材料にもならない。
           */}
          <Section
            title="接続先"
            description={
              canEndpoint
                ? '自社で契約している LLM サービスへ直接つなぐ設定です。空欄なら運営が用意した共有の接続先を使います。'
                : 'ご利用中のプランでは接続先を指定できません。運営が用意した共有の接続先を使います。'
            }
          >
            <div className="space-y-4">
              <KeyValue
                columns={2}
                items={[
                  { key: '実際に使われる提供元', value: labelOf(data.effective.provider) },
                  {
                    key: '実際に使われるモデル',
                    value: data.effective.model || '（提供元の既定）',
                  },
                ]}
              />

              <Field label="モデル名（任意）" htmlFor="ai-model" hint="空欄なら既定のモデルを使います。">
                <Input
                  id="ai-model"
                  value={draft.model}
                  disabled={locked || !draft.enabled}
                  onChange={(e) => set('model', e.target.value)}
                  placeholder="例: gpt-4o-mini"
                />
              </Field>

              {canEndpoint ? (
                <>
                  <Field
                    label="接続先の URL"
                    htmlFor="ai-endpoint"
                    hint="OpenAI 互換 API のベース URL。社内に設置したサーバーも指定できます。"
                  >
                    <Input
                      id="ai-endpoint"
                      value={draft.endpoint}
                      disabled={locked || !draft.enabled}
                      onChange={(e) => set('endpoint', e.target.value)}
                      placeholder="https://api.example.com/v1"
                    />
                  </Field>

                  <Field
                    label="API キー"
                    htmlFor="ai-apikey"
                    hint={
                      data.credential.hasKey
                        ? `登録済み（指紋 ${data.credential.fingerprint ?? ''}）。変更するときだけ入力してください。`
                        : '暗号化して保管します。保存後に画面へ再表示されることはありません。'
                    }
                  >
                    <Input
                      id="ai-apikey"
                      type="password"
                      value={apiKey}
                      disabled={locked || !draft.enabled}
                      onChange={(e) => setApiKey(e.target.value)}
                      autoComplete="off"
                      placeholder={data.credential.hasKey ? '変更しない' : ''}
                    />
                  </Field>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="希望する処理リージョン">
                      <Select
                        value={draft.preferredRegion}
                        onValueChange={(v) => set('preferredRegion', v as AiPreferredRegion)}
                      >
                        <SelectTrigger disabled={locked || !draft.enabled}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_PREFERRED_REGION.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r === 'auto' ? 'おまかせ（最寄り）' : labelOf(r)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field
                      label="データを置く国の指定"
                      hint="指定すると、その法域の接続先が使えないときは AI を停止します（別の国へは流しません）。"
                    >
                      <Select
                        value={draft.requireResidency}
                        onValueChange={(v) =>
                          set('requireResidency', v as AiResidencyRequirement)
                        }
                      >
                        <SelectTrigger disabled={locked || !draft.enabled}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_RESIDENCY_REQUIREMENT.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r === 'any' ? '指定なし' : labelOf(r)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                </>
              ) : (
                <Alert variant="info">
                  <AlertTitle>接続先の指定は上位プランの機能です</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      自社で契約している LLM サービスや社内に設置したサーバーへ接続したい場合は、
                      プランの変更が必要です。
                    </p>
                    <Button asChild variant="outline" size="sm">
                      <AppLink to="/billing/plan">プランを見る</AppLink>
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </Section>

          {locked ? (
            <p className="text-xs text-muted-foreground">
              {data.lockedByOps
                ? '運営により固定されているため変更できません。'
                : !data.plan.aiAssistant
                  ? 'ご利用中のプランでは AI 機能を設定できません。'
                  : 'この設定を変更する権限がありません。組織の管理者にご依頼ください。'}
            </p>
          ) : (
            <SubmitBar submitLabel="保存する" pending={save.isPending} />
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * `PUT /ai/policy` の本文を組み立てる。
 *
 * **プランで指定できない項目を入れない。** `aiCustomEndpoint` が false の
 * 組織が `provider` / `endpoint` / `preset` / `apiKey` / `requireResidency` を
 * 1 つでも送ると、サーバは 403（`fields` 付き）を返して**保存全体が失敗する**。
 * 値が既定のままでも「送った」ことが理由で弾かれるので、キーごと落とす。
 */
export function buildPatch(
  draft: OrgAiPolicy,
  apiKey: string,
  canEndpoint: boolean,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    enabled: draft.enabled,
    model: draft.model,
    features: draft.features,
    privacyLevel: draft.privacyLevel,
    monthlyTokenCap: draft.monthlyTokenCap,
    onLimit: draft.onLimit,
    logRetentionDays: draft.logRetentionDays,
    preferredRegion: draft.preferredRegion,
  };
  if (!canEndpoint) return base;
  return {
    ...base,
    provider: draft.provider,
    endpoint: draft.endpoint,
    preset: draft.preset,
    requireResidency: draft.requireResidency,
    // 空文字は「鍵を削除する」の意味を持つので、**入力があったときだけ**送る。
    ...(apiKey.length > 0 ? { apiKey } : {}),
  };
}

/* ------------------------------------------------------------- 使用量 */

/** JST の当月（`yyyyMM`）。API の既定と揃える。 */
export function currentMonthJst(): string {
  const now = new Date();
  const jst = new Date(now.getTime() + (now.getTimezoneOffset() + 9 * 60) * 60_000);
  return `${jst.getFullYear()}${String(jst.getMonth() + 1).padStart(2, '0')}`;
}

function AiUsageCard({ cap }: { cap: number }) {
  const [month, setMonth] = React.useState(currentMonthJst);
  const usage = useQuery({
    queryKey: qk.aiUsage(month),
    queryFn: () => api.get<AiUsageView>(`/ai/usage?month=${encodeURIComponent(month)}`),
  });

  const months = React.useMemo(() => recentMonths(12), []);

  const columns: Array<DataTableColumn<AiUsageView['items'][number]>> = [
    { id: 'date', header: '日付', width: 'w-28', cell: (r) => r.usageDate },
    {
      id: 'model',
      header: 'モデル',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm">{r.model || '—'}</p>
          <p className="truncate text-xs text-muted-foreground">{labelOf(r.provider)}</p>
        </div>
      ),
    },
    {
      id: 'task',
      header: '機能',
      width: 'w-40',
      hideBelow: 'md',
      cell: (r) => AI_FEATURE_LABELS_JA[r.task] ?? labelOf(r.task),
    },
    {
      id: 'calls',
      header: '回数',
      width: 'w-24',
      align: 'right',
      cell: (r) => (
        <span className="tabular text-xs">
          {formatCount(r.calls)}
          {r.failedCalls > 0 ? (
            <Badge variant="muted" size="sm" className="ml-1">
              失敗 {formatCount(r.failedCalls)}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      id: 'tokens',
      header: 'トークン',
      width: 'w-32',
      align: 'right',
      cell: (r) => (
        <span className="tabular text-xs">{formatCount(r.inputTokens + r.outputTokens)}</span>
      ),
    },
  ];

  const total = usage.data?.total;

  return (
    <div className="space-y-4">
      <Field label="対象の月" className="max-w-[12rem]">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m} value={m}>
                {`${m.slice(0, 4)} 年 ${Number(m.slice(4, 6))} 月`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {total ? (
        <Card>
          <CardContent className="space-y-3 p-5">
            <UsageBar
              used={total.totalTokens}
              total={cap}
              label="今月のトークン"
              formatValue={(n) => formatCount(n)}
            />
            <KeyValue
              columns={3}
              items={[
                { key: '渡した分', value: formatCount(total.inputTokens) },
                { key: '返ってきた分', value: formatCount(total.outputTokens) },
                { key: '呼び出し回数', value: formatCount(total.calls) },
              ]}
            />
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        columns={columns}
        rows={usage.data?.items ?? []}
        rowKey={(r, i) => `${r.usageDate}-${r.provider}-${r.model}-${r.task}-${i}`}
        loading={usage.isPending}
        error={usage.isError ? '使用量を取得できませんでした。' : undefined}
        emptyTitle="この月の利用はありません"
        emptyDescription="AI 機能が使われると、日ごとの内訳がここに出ます。"
      />
    </div>
  );
}

/**
 * 直近 n か月の `yyyyMM`（新しい順）。
 *
 * **起点は必ず JST の当月**（`currentMonthJst()`）。以前はここだけ
 * ブラウザのローカル時刻（`new Date().getFullYear()`）で数えていたため、
 * JST 以外の時間帯で月初の数時間だけ**選択中の月が選択肢に無い**状態に
 * なった（`useState(currentMonthJst)` は JST の当月を選ぶのに、一覧の
 * 先頭は前月）。`<Select>` は値が選択肢に無いと空欄で描かれるので、
 * 「月を選び直せないが表は出ている」という直しようのない画面になる。
 */
export function recentMonths(n: number, from: string = currentMonthJst()): string[] {
  const out: string[] = [];
  let year = Number(from.slice(0, 4));
  let month = Number(from.slice(4, 6));
  for (let i = 0; i < n; i += 1) {
    out.push(`${year}${String(month).padStart(2, '0')}`);
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return out;
}
